import { useCallback, useEffect, useState } from "react";

/**
 * Hook that uses IntersectionObserver to detect when an element has been
 * visible in the viewport. Once visible, it stays "visible" permanently
 * (one-shot). Used for lazy rendering of heavy content like Markdown.
 *
 * This uses a callback ref so it can start observing when the element
 * is mounted (e.g., inside Radix Accordion content).
 *
 * @param rootMargin – IntersectionObserver rootMargin (default "200px").
 * @param initiallyVisible – When true the element is treated as already
 *   visible, skipping the IntersectionObserver entirely. Useful for the
 *   first few items that are guaranteed to be in the viewport on mount
 *   but may be missed by the observer due to CSS animations (e.g. Radix
 *   Accordion opening from height 0).
 */
export function useLazyRender(rootMargin = "200px", initiallyVisible = false) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(initiallyVisible);

  const ref = useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (hasBeenVisible) return;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setHasBeenVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasBeenVisible, node, rootMargin]);

  return { ref, hasBeenVisible };
}
